<section class="go-container">

  <go-card class="go-column go-column--100" id="server-integration">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Integrating with a Server</h2>
      <go-copy cardId="server-integration" goCopyCardLink></go-copy>
    </ng-container>
    <p class="go-body-copy go-body-copy--no-margin" go-card-content>
      By default the table component is setup for client side
      data manipulation. What if there is too much data to send
      to the front end? Implementing server-side data manipulation
      is what is required.
      <br>
      <caption>(This means paging and sorting)</caption>
    </p>
  </go-card>

  <go-card class="go-column go-column--100" id="setup">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Setting it up</h2>
      <go-copy cardId="setup" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          For server-side integration to work properly, the
          following must be present on go-table:
        </p>
        <ol class="go-list go-list--ordered">
          <li><code class="code-block--inline">(tableChange)</code> - to handle the table events</li>
          <li><code class="code-block--inline">[loadingData]</code> - to handle the loading of the table</li>
          <li><code class="code-block--inline">[tableConfig]</code> - because it's required</li>
        </ol>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">example.html</h4>
        <code [highlight]="tableConfig_html"></code>
      </div>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          There are 5 properties of the table config that are
          important when building out server-side integration:
        </p>
        <ol class="go-list go-list--ordered">
          <li><code class="code-block--inline">pageConfig.offset</code> - the current place in the data set</li>
          <li><code class="code-block--inline">pageConfig.perPage</code> - the current number of items on each page</li>
          <li><code class="code-block--inline">searchConfig.searchTerm</code> - the current search term (if table search has value)</li>
          <li><code class="code-block--inline">sortConfig.column</code> - the current column that is sorted (if table is sorted)</li>
          <li><code class="code-block--inline">sortConfig.direction</code> - the current sort direction (if table is sorted)</li>
        </ol>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h4 class="go-heading-4">example.ts</h4>
        <code [highlight]="tableConfig_ts"></code>
        <p class="go-body-copy">
          It is recommended to pass the entire <code class="code-block--inline">GoTableConfig</code>
          to the service that is retrieving the data and map
          the properties of the object to the corresponding
          parameters of the endpoint that is being hit.
        </p>
        <p class="go-body-copy">
          When the data is successfully returned, the properties
          shown below must be set.
        </p>
        <code [highlight]="tableConfig_ts_tableChange" class="code-block--no-bottom-margin"></code>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="important-notes">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Important Notes</h2>
      <go-copy cardId="important-notes" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">Total Results</h4>
        <p class="go-body-copy">
          For the best user experience with server-side integrations,
          the data returned from the server should have two properties:
        </p>
        <code [highlight]="server_response"></code>
        <p class="go-body-copy go-body-copy--no-margin">
          These properties should then be set, upon a successful response,
          to the corresponding properties on the <code class="code-block--inline">GoTableConfig</code>,
          <code class="code-block--inline">tableData</code> and <code class="code-block--inline">totalCount</code>.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">[loadingData]</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          When server mode is enabled, this property will be set to 
          <code class="code-block--inline">true</code> whenever the
          <code class="code-block--inline">tableChange</code> event
          is fired. The responsibility to set it back to
          <code class="code-block--inline">false</code> falls upon
          on the parent component after data is received from the server.
          This is to provide a consistent and helpful experience to
          users and should not be overridden.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h4 class="go-heading-4">(tableChange)</h4>
        <p class="go-body-copy">
          The <code class="code-block--inline">tableChange</code>
          event is emitted anytime a trigger is fired in the table
          component. These triggers are:
        </p>
        <ol class="go-list go-list--ordered">
          <li>Any page change</li>
          <li>Page size change</li>
          <li>Sort change</li>
          <li>Search change</li>
        </ol>
      </div>

    </div>
  </go-card>
</section>
